import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {PageBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-background-option';
import {PageTypeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-type-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {PageGridOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-grid-option';
import {PageFoldsOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-folds-option';
import {PageBleedOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-bleed-option';
import {DesignNameOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/design-name-option';
import {
  AI_WRITER_VIA_DEFAULT_POSTER_CONTEXTUAL_OPTIONS,
  PosterEditorDesignSubPanels,
} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-default-options/poster-editing-default-options.types';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {getCurrentPageBackground, getCurrentPageOrder, getNumberOfPages} from '@Components/poster-editor/poster-editor-reducer';
import {ContextualMenu} from '@Components/contextual-menu';
import {PageBackgroundColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-background-color-panel';
import {SuperUserPosterControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/super-user-poster-controls/super-user-poster-controls';
import {ImageBackgroundOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-background-options';
import {ImageBgTintColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-bg-tint-color-panel';
import {ImageBgMultiplyColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-bg-multiply-color-panel';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {PageUnlockAllItemsOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-unlock-all-items-option';
import {DesignerAttribution} from '@Components/poster-editor/components/poster-editing-side-panel/components/designer-attribution';
import {AiSubtitleState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import AiSubtitlePanel from '@Panels/ai-subtitle-panel/ai-subtitle-panel';
import {useAppSelector} from '@/hooks';
import styles from './poster-editing-default-options.module.scss';
import {PosterGenerateAiSubtitlesOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/generate-ai-subtitles-option/components/poster-generate-ai-subtitles-option/poster-generate-ai-subtitles-option';
import {AiTextReWriteOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/ai-text-re-write-option/ai-text-re-write-option';
import {PageIntroAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-intro-animation-panel/page-intro-animation-panel';
import {PageOutroAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-outro-animation-panel/page-outro-animation-panel';
import {AnimationPanelType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {PageAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-animation-option/page-animation-option';
import {PageAlignmentGuidesOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-alignment-guides-option/page-alignment-guides-options';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';

export function PosterEditingDefaultOptions(): ReactElement {
  const [currentlyOpenedSubPanel, setCurrentlyOpenedSubPanel] = useState(PosterEditorDesignSubPanels.NONE);
  const totalNumberOfPages = useAppSelector(getNumberOfPages);
  const currentPageOrder = useAppSelector(getCurrentPageOrder);
  const currentBackgroundObject = useAppSelector(getCurrentPageBackground);

  const aiSubtitleState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });

  useEffect(() => {
    if (aiSubtitleState === AiSubtitleState.IDLE) {
      setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
    }
  }, [aiSubtitleState]);

  useEffect(() => {
    if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_IMAGE && currentBackgroundObject?.details.type === BackgroundTypeName.COLOR) {
      setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
    }
    if (
      currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_COLOR &&
      (!doesBackgroundHaveColor() || currentBackgroundObject?.details.type === BackgroundTypeName.IMAGE)
    ) {
      setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
    }
  }, [currentBackgroundObject?.details]);

  const doesBackgroundHaveColor = (): boolean => {
    if (currentBackgroundObject?.details.type === BackgroundTypeName.IMAGE) {
      return false;
    }

    return currentBackgroundObject?.details.fill.fillType !== FillTypes.NONE;
  };

  const getSubPanels = (): ReactElement => {
    switch (currentlyOpenedSubPanel) {
      case PosterEditorDesignSubPanels.BACKGROUND_COLOR:
        return <PageBackgroundColorPanel />;
      case PosterEditorDesignSubPanels.BACKGROUND_IMAGE:
        return <ImageBackgroundOptions onClickColorFill={setCurrentlyOpenedSubPanel} />;
      case PosterEditorDesignSubPanels.BACKGROUND_IMAGE_TINT_COLOR:
        return <ImageBgTintColorPanel />;
      case PosterEditorDesignSubPanels.BACKGROUND_IMAGE_MULTIPLY_COLOR:
        return <ImageBgMultiplyColorPanel />;
      case PosterEditorDesignSubPanels.INTRO_ANIMATION_STYLES:
        return <PageIntroAnimationPanel />;
      case PosterEditorDesignSubPanels.OUTRO_ANIMATION_STYLES:
        return <PageOutroAnimationPanel />;
      case PosterEditorDesignSubPanels.AI_SUBTITLES:
        return (
          <AiSubtitlePanel
            isOpenedInContextualMenu
            onDone={() => {
              setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
            }}
            onBack={() => {
              setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
            }}
            contentClasses={styles.content}
            hideHeader
          />
        );
      default:
        throw new Error('unhandled color panel option for default poster editing options');
    }
  };

  const handleBackButtonForHeader = (): void => {
    if (
      currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_IMAGE_TINT_COLOR ||
      currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_IMAGE_MULTIPLY_COLOR
    ) {
      setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.BACKGROUND_IMAGE);
      return;
    }
    setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
  };

  const getHeader = (): ReactElement => {
    let text = window.i18next.t('pmwjs_design');

    if (totalNumberOfPages > 1) {
      text = window.i18next.t('pmwjs_page_number', {pageNumber: currentPageOrder ? currentPageOrder + 1 : 1});
    }

    if (isSubPanelOpened()) {
      if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.AI_SUBTITLES) {
        text = window.i18next.t('pmwjs_ai_subtitles');
      } else if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.INTRO_ANIMATION_STYLES) {
        text = window.i18next.t('pmwjs_entrance_animation');
      } else if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.OUTRO_ANIMATION_STYLES) {
        text = window.i18next.t('pmwjs_exit_animation');
      } else {
        text = window.i18next.t('pmwjs_color');
      }
    }

    if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_IMAGE) {
      text = window.i18next.t('pmwjs_background_image');
    }
    return (
      <ContextualMenuHeader id="default-options-header">
        <ContextualMenuTextHeader
          showBackButton={isSubPanelOpened()}
          onBackButton={(): void => {
            handleBackButtonForHeader();
          }}
          text={text}
          className={currentlyOpenedSubPanel === PosterEditorDesignSubPanels.AI_SUBTITLES ? 'content-ai' : ''}
        />
      </ContextualMenuHeader>
    );
  };

  const isSubPanelOpened = (): boolean => {
    return currentlyOpenedSubPanel !== PosterEditorDesignSubPanels.NONE;
  };

  const getSuperUserOptions = (): ReactElement | null => {
    if (window.posterEditor?.config?.showSuperUserOptions || window.PMW.isStorybook()) {
      return (
        <>
          <ContextualMenuHeadingOption id="default-super-user-options-heading" text={window.i18next.t('pmwjs_super_user_options')} />
          <SuperUserPosterControls />
        </>
      );
    }

    return null;
  };

  const getOptions = (): ReactElement => {
    if (isSubPanelOpened()) {
      return getSubPanels();
    }

    return (
      <>
        <PageUnlockAllItemsOption />
        <PageTypeOption />
        <AiTextReWriteOption id={AI_WRITER_VIA_DEFAULT_POSTER_CONTEXTUAL_OPTIONS} showNewTag />
        <PageBackgroundOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.BACKGROUND_COLOR);
          }}
          onClickEditImageBackground={(): void => {
            setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.BACKGROUND_IMAGE);
          }}
        />
        <PosterGenerateAiSubtitlesOption
          onClickGenerate={() => {
            setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.AI_SUBTITLES);
          }}
        />
        <PageAnimationOption
          onAnimationTypeClick={(type: AnimationPanelType): void => {
            setCurrentlyOpenedSubPanel(
              type === AnimationPanelType.INTRO_ANIMATION_STYLES ? PosterEditorDesignSubPanels.INTRO_ANIMATION_STYLES : PosterEditorDesignSubPanels.OUTRO_ANIMATION_STYLES
            );
          }}
        />
        <ContextualMenuHeadingOption id="default-title-heading" text={window.i18next.t('pmwjs_title')} />
        <DesignNameOption />
        <ContextualMenuHeadingOption id="default-layout-heading" text={window.i18next.t('pmwjs_layout')} />
        <PageGridOption />
        <PageFoldsOption />
        <PageBleedOption />
        <PageAlignmentGuidesOptions />
        <DesignerAttribution />
        {getSuperUserOptions()}
      </>
    );
  };

  const onOutsideClick = (): void => {
    if (isSubPanelOpened()) {
      setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
    }
  };

  const getKey = (): string => {
    if (!isSubPanelOpened()) {
      return ContextualMenuContentKeys.DEFAULT;
    }

    return '';
  };

  return (
    <ContextualMenu
      id="poster-editing-side-panel"
      onOutsideClick={onOutsideClick}
      includeLeftRightPadding={!isSubPanelOpened() || currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_IMAGE}
      header={getHeader()}
      fireOnOutsideClickOnCanvasClick={false}
      contentKey={getKey()}
    >
      {getOptions()}
    </ContextualMenu>
  );
}
